import { useState, useRef, useEffect } from "react";
import { defaultRequiredMessage } from "./getErrorMessages";

if (process.env.NODE_ENV === "development" && !defaultRequiredMessage) {
  console.warn(
    "You now need to export the `defaultRequiredMessage` in `theme/components/atoms/Form/Input/getErrorMessages.js`."
  );
}

const isEmpty = (value) => {
  return typeof value === "undefined" || value === "";
};

const getInitialPristineValue = (value, errors) =>
  isEmpty(value) || !errors || errors.length === 0;

const useFormInputState = ({
  forceDirty,
  required,
  value,
  errors,
  requiredMessage = defaultRequiredMessage,
}) => {
  const [isPristineLocally, setPristineLocally] = useState(
    getInitialPristineValue(value, errors)
  );
  const isPristine = !forceDirty && isPristineLocally;

  const prevPropsRef = useRef({ initialValue: value, errors });
  useEffect(() => {
    // Some implementations compute errors asynchronously
    // We wait for the errors to be computed before re-initializing
    // the pristine's value
    if (
      isPristine &&
      value === prevPropsRef.current.initialValue &&
      errors !== prevPropsRef.current.errors
    ) {
      setPristineLocally(getInitialPristineValue(value, errors));
    }

    prevPropsRef.current.errors = errors;
  }, [isPristine, value, errors]);

  const enhancedErrors = !isPristine
    ? []
        .concat(errors || [])
        .concat(
          (errors || []).length === 0 && // If the required error is not already handled by the implementation's error mechanism
            required &&
            (!value || value.length === 0)
            ? [requiredMessage]
            : []
        )
        .filter((error) => typeof error !== "string" || error.length > 0)
    : [];

  return {
    onBlur: () => {
      setPristineLocally(false);
    },
    isPristine: () => isPristine,
    isDirty: () => !isPristine,
    isValid: () => !isPristine && enhancedErrors.length === 0,
    getErrors: () => enhancedErrors,
  };
};

export default useFormInputState;
